import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import DateRangePicker from "../components/DateRangePicker";
import ChannelSelector from "../components/ChannelSelector";
import ToneScoreChart from "../components/ToneScoreChart";
import ToneDistributionChart from "../components/ToneDistributionChart";
import ChannelToneTable from "../components/ChannelToneTable";
import SyncButtonWithTimestamp from '../components/SyncButtonWithTimestamp';
import EmptyState from '../components/EmptyState';

// Import Slack data service functions
import {
  fetchOrganization,
  checkSlackConnection,
  fetchSlackChannels,
  clearCache
} from "../utils/slackDataService";

// Import report service functions
import {
  fetchReport,
  calculateToneTimeData,
  calculateToneDistributionDataWithCounts,
  calculateChannelToneData
} from "../utils/slackReportService";

import { shouldBypassSlackConnection } from "../utils/config";

const Tone = () => {
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [slackConnected, setSlackConnected] = useState(false);
  const [availableChannels, setAvailableChannels] = useState([]);
  const [noDataAvailable, setNoDataAvailable] = useState(false);
  
  // Initialize dateRange with "last30days"
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - 29);
    const endDate = new Date(today);
    return { startDate, endDate, range: "last30days" };
  });
  
  const [selectedChannels, setSelectedChannels] = useState([]);
  
  // Data states
  const [toneScoreData, setToneScoreData] = useState([]);
  const [toneDistributionData, setToneDistributionData] = useState([]);
  const [channelToneData, setChannelToneData] = useState([]);
  
  // Check user session and load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      // Retrieve user data from localStorage
      const userData = localStorage.getItem("user");
      if (userData && userData !== "undefined") {
        try {
          setLoading(true);
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          
          if (parsedUser.organizations && parsedUser.organizations.length > 0) {
            const firstOrgId = parsedUser.organizations[0].organizationId;
            const org = await fetchOrganization(firstOrgId);
            setOrganization(org);
          }
        } catch (error) {
          console.error("Error parsing user data:", error);
          setError("Error loading user data. Please try again.");
        } finally {
          setLoading(false);
        }
      } else {
        navigate("/login");
      }
    };
    
    loadInitialData();
  }, [navigate]);
  
  // Check Slack connection and load Slack data
  useEffect(() => {
    const loadSlackData = async () => {
      if (!organization) {
        console.log("No organization available yet, waiting...");
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        console.log("Loading Slack data for organization:", organization._id);
        
        // If we should bypass, set slackConnected to true and proceed
        if (shouldBypassSlackConnection()) {
          console.log('Tone.jsx: Bypassing Slack check, setting slackConnected=true');
          setSlackConnected(true);
          const channels = await fetchSlackChannels(organization);
          console.log("Loaded channels:", channels.length);
          setAvailableChannels(channels);
        } else {
          // Check if Slack is connected
          const isConnected = await checkSlackConnection(organization);
          console.log("Slack connection status:", isConnected);
          setSlackConnected(isConnected);
          
          if (isConnected) {
            // Load channels
            const channels = await fetchSlackChannels(organization);
            console.log("Loaded channels:", channels.length);
            setAvailableChannels(channels);
          } else {
            setError("Slack is not connected. Please connect Slack in the Integrations page.");
          }
        }
      } catch (error) {
        console.error("Error loading Slack data:", error);
        if (!shouldBypassSlackConnection()) {
          setError("Error checking Slack connection. Please try again.");
        }
      } finally {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    };
    
    loadSlackData();
  }, [organization]);

  // Load tone data when slackConnected becomes true
  useEffect(() => {
    if (slackConnected && organization && initialLoadComplete) {
      // Wait a bit to ensure all state is set before loading data
      setTimeout(async () => {
        await loadToneData(false); // false means this is not a filter change
      }, 100);
    }
  }, [slackConnected, organization, initialLoadComplete]);
  
  // Load tone data based on filters (copied from sentiment analysis pattern)
  const loadToneData = async (isFilterChange = true, channelsOverride = null) => {
    if (!organization || !slackConnected) {
      console.log("Cannot load data: organization or slack not connected", { organization: !!organization, slackConnected });
      return;
    }
    
    try {
      if (isFilterChange) {
        setDataLoading(true);
      } else {
        setLoading(true);
      }
      setError(null);
      setNoDataAvailable(false);
      
      // Force clear the cache when loading fresh data with filters
      clearCache();
      
      // Use channelsOverride if provided, otherwise use selectedChannels state
      const channelsToUse = channelsOverride !== null ? channelsOverride : selectedChannels;
      
      console.log("Loading tone data with filter:", { 
        dateRange, 
        selectedChannels: channelsToUse,
        isFilterChange 
      });
      
      // Ensure we have valid date objects
      const startDate = new Date(dateRange.startDate);
      const endDate = new Date(dateRange.endDate);
      
      console.log(`Date range: ${startDate.toLocaleDateString()} to ${endDate.toLocaleDateString()}`);
      
      // Get channel names to filter by (since backend uses names as keys)
      const channelNames = channelsToUse.length > 0 
        ? channelsToUse
        : null;
      
      // First, fetch the report data from the database
      const reportResult = await fetchReport(organization, {
        channels: channelNames
      });
      
      // Calculate tone data from the database report data
      let timeData = [];
      let distributionData = [];
      let channelData = [];
      
      if (reportResult && reportResult.success && reportResult.data) {
        const startDateStr = startDate.toISOString().split('T')[0];
        const endDateStr = endDate.toISOString().split('T')[0];
        
        timeData = calculateToneTimeData(reportResult.data.reportData, startDateStr, endDateStr);
        distributionData = calculateToneDistributionDataWithCounts(reportResult.data.reportData, startDateStr, endDateStr);
        channelData = calculateChannelToneData(reportResult.data.reportData, startDateStr, endDateStr);
        
        // Enrich channel data with is_private property
        try {
          const availableChannels = await fetchSlackChannels(organization);
          const channelMap = new Map(availableChannels.map(ch => [ch.name, ch.is_private]));
          channelData = channelData.map(item => ({
            ...item,
            is_private: channelMap.get(item.channel) || false
          }));
        } catch (error) {
          console.warn("Could not fetch channels for is_private property:", error);
        }
      }
      
      console.log(`Loaded data: ${timeData.length} time points, ${distributionData.length} distribution items, ${channelData.length} channels`);
      
      setToneScoreData(timeData);
      setToneDistributionData(distributionData);
      setChannelToneData(channelData);
      
      // Check if we have any real data
      const toneKeys = ['collaborative', 'creative', 'formal', 'assertive', 'urgent', 'informal', 'casual', 'technical'];
      const hasTimeData = timeData.some(item => toneKeys.some(k => (item[k] || 0) > 0));
      const hasDistributionData = distributionData.some(item => item.value > 0);
      const hasChannelData = channelData.length > 0;
      
      console.log("Data status:", { hasTimeData, hasDistributionData, hasChannelData });
      console.log("Sample time data:", timeData.slice(0, 3));
      console.log("Sample distribution data:", distributionData.slice(0, 3));
      console.log("Sample channel data:", channelData.slice(0, 2));
      
      if (!hasTimeData && !hasDistributionData && !hasChannelData) {
        setNoDataAvailable(true);
      } else {
        setNoDataAvailable(false);
      }
      
    } catch (error) {
      console.error("Error loading tone data:", error);
      setError("Error loading tone data. Some data may be incomplete.");
    } finally {
      setLoading(false);
      setDataLoading(false);
    }
  };
  
  // Handle date range change - stabilized with useCallback
  const handleDateRangeChange = useCallback((newRange) => {
    console.log("Date range changed:", newRange);
    
    // Validate the date range before proceeding
    if (!newRange || !newRange.startDate || !newRange.endDate) {
      console.warn("Invalid date range received:", newRange);
      return;
    }
    
    // Update UI state first
    setToneScoreData([]);
    setToneDistributionData([]);
    setChannelToneData([]);
    setDataLoading(true);
    
    // Update the date range state
    setDateRange(newRange);
  }, []);

  // Load data when dateRange changes (for filter changes)
  useEffect(() => {
    if (dateRange && organization && slackConnected && initialLoadComplete) {
      // Use setTimeout to ensure state is updated before loading data
      setTimeout(() => {
        loadToneData(true);
      }, 0);
    }
  }, [dateRange, organization, slackConnected, initialLoadComplete]);
  
  // Handle channel selection change
  const handleChannelChange = (channels) => {
    setSelectedChannels(channels);
    loadToneData(true, channels); // Pass the new channels directly
  };
  
  // Filter channel data based on selection
  const filteredChannelData = selectedChannels.length > 0
    ? channelToneData.filter(channel => selectedChannels.includes(channel.channel))
    : channelToneData;
  
  // Show loading indicator for the entire page during initial load
  if (loading && !initialLoadComplete) {
    return (
      <div className="flex h-screen h-dvh overflow-hidden bg-grey-10">
        <Sidebar user={user} organization={organization} organizations={organizations} />
        <div className="flex-1 p-6 flex items-center justify-center">
          <div className="text-center">
            <div className="w-10 h-10 border-4 border-grey-40 border-t-brand rounded-full animate-spin inline-block mb-4"></div>
            <p className="text-grey-60">Loading tone data...</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex h-screen h-dvh overflow-hidden bg-bg-gradient-dash">
      <Sidebar user={user} organization={organization} organizations={organizations} />
      
      {/* Main content area */}
      <div className="flex-1 overflow-y-auto">
        {/* Header and filters */}
        <div className="p-5 pb-0">
          <h1 className="text-xl font-normal text-black-0">Tone</h1>
         
        </div>
        
        {/* Slack not connected message */}
        {!slackConnected && !loading && (
          <div className="mx-5">
            <EmptyState
              variant="no-connection"
              title="Slack Not Connected"
              description="You need to connect to Slack first to analyze the tone of your team's communications."
              actionButton={
                <button 
                  onClick={() => navigate('/settings')}
                  className="bg-brand text-white px-6 py-2 rounded-md hover:bg-brand-dark transition-colors font-medium"
                >
                  Go to Settings
                </button>
              }
            />
          </div>
        )}
        
        {/* Error message */}
        {error && (
          <div className="bg-red-light p-4 rounded-md mx-5 my-4">
            <p className="text-red-strong">{error}</p>
          </div>
        )}
        

        
        {/* Main content - only show if Slack is connected and data is loaded */}
        {slackConnected && (
          <>
            {/* Filters */}
            <div className="flex items-center justify-between p-5">
              <div className="flex items-center gap-3">
                <DateRangePicker 
                  dateRange={dateRange} 
                  onChange={handleDateRangeChange} 
                />
                <ChannelSelector 
                  availableChannels={availableChannels}
                  selectedChannels={selectedChannels}
                  onChange={handleChannelChange}
                />
              </div>
              <SyncButtonWithTimestamp organization={organization} dateRange={dateRange} onSyncComplete={() => window.location.reload()} />
            </div>
            
            {/* Loading indicator for filter changes */}
            {dataLoading && (
              <div className="flex justify-center items-center py-4 mb-4">
                <div className="w-6 h-6 border-2 border-grey-40 border-t-brand rounded-full animate-spin mr-3"></div>
                <span className="text-grey-60">Updating data...</span>
              </div>
            )}
            
            {/* Charts row */}
            <div className="flex gap-4 p-5 pt-0">
              <div className="flex-1">
                <ToneScoreChart data={toneScoreData} />
              </div>
              <div style={{ width: '400px' }}>
                <ToneDistributionChart data={toneDistributionData} />
              </div>
            </div>
            
            {/* Channel tone table */}
            <ChannelToneTable data={filteredChannelData} />
            
            {/* Empty data message when tables/charts are empty */}
            {(filteredChannelData.length === 0 || noDataAvailable) && !dataLoading && (
              <div className="mt-4">
                <EmptyState
                  variant="search"
                  title="No channel data matches your filters"
                  description="Try adjusting your date range or channel selection to see tone data."
                  actionButton={null}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Tone; 